/**
 * Decoder Store
 * Production-ready Zustand store for decoder state management
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  DecoderOperationType,
  DecoderStep,
} from '../components/decoder/types';
import { executeOperation } from '../components/decoder/utils/operations';

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

const logStoreFailure = ({
  action,
  error,
}: {
  action: string;
  error: unknown;
}) => {
  console.error(`Decoder store action failed: ${action}`, toError(error));
};

type DecoderStore = {
  steps: DecoderStep[];
  initialInput: string;
  selectedOperation: DecoderOperationType;
  inputHeight: number | null;
  _recalcVersion: number;

  setInitialInput: (input: string) => void;
  setSelectedOperation: (operation: DecoderOperationType) => void;
  setInputHeight: (height: number) => void;
  addStep: ({
    operation,
    afterStepId,
  }: {
    operation: DecoderOperationType;
    afterStepId?: string;
  }) => void;
  removeStep: (stepId: string) => void;
  updateStepOperation: ({
    stepId,
    operation,
  }: {
    stepId: string;
    operation: DecoderOperationType;
  }) => void;
  toggleStepCollapse: (stepId: string) => void;
  clearAllSteps: () => void;
  recalculateFromStep: (stepId: string) => void;
};

export const useDecoderStore = create<DecoderStore>()(
  persist(
    (set, get) => ({
      steps: [],
      initialInput: '',
      selectedOperation: 'url-decode',
      inputHeight: null,
      _recalcVersion: 0,

      setInputHeight: (height) => {
        set({ inputHeight: height });
      },

      setInitialInput: (input) => {
        if (input === '') {
          set({
            initialInput: '',
            steps: [],
            selectedOperation: 'url-decode',
            _recalcVersion: get()._recalcVersion + 1,
          });
          return;
        }

        const { steps } = get();
        set({ initialInput: input });

        if (steps.length === 0) {
          return;
        }

        const version = get()._recalcVersion + 1;
        set({ _recalcVersion: version });

        void recalculateSteps(steps, 0, input)
          .then((recalculatedSteps) => {
            if (get()._recalcVersion !== version) return;
            set({ steps: recalculatedSteps });
          })
          .catch((error: unknown) => {
            logStoreFailure({ action: 'setInitialInput', error });
          });
      },

      setSelectedOperation: (operation) => {
        set({ selectedOperation: operation });
      },

      addStep: ({ operation, afterStepId }) => {
        const { steps, initialInput } = get();

        let input = initialInput;
        let insertIndex = steps.length;

        if (afterStepId) {
          const afterStepIndex = steps.findIndex(
            (step) => step.id === afterStepId,
          );
          if (afterStepIndex !== -1) {
            input = steps[afterStepIndex].output;
            insertIndex = afterStepIndex + 1;
          }
        } else if (steps.length > 0) {
          input = steps[steps.length - 1].output;
        }

        const version = get()._recalcVersion + 1;
        set({ _recalcVersion: version });

        void executeOperation({ type: operation, input })
          .then((result) => {
            if (get()._recalcVersion !== version) return;

            const newStep: DecoderStep = {
              id: `step-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
              operation,
              input,
              output: result.success ? result.output : input,
              error: result.error,
              isCollapsed: false,
              timestamp: Date.now(),
            };

            const currentSteps = get().steps;
            const newSteps = [...currentSteps];
            const safeIndex = Math.min(insertIndex, newSteps.length);
            newSteps.splice(safeIndex, 0, newStep);

            return recalculateSteps(
              newSteps,
              safeIndex + 1,
              get().initialInput,
            );
          })
          .then((recalculatedSteps) => {
            if (!recalculatedSteps || get()._recalcVersion !== version) return;
            set({ steps: recalculatedSteps });
          })
          .catch((error: unknown) => {
            logStoreFailure({ action: 'addStep', error });
          });
      },

      removeStep: (stepId) => {
        const { steps, initialInput } = get();
        const stepIndex = steps.findIndex((step) => step.id === stepId);

        if (stepIndex === -1) return;

        const newSteps = steps.filter((step) => step.id !== stepId);
        const version = get()._recalcVersion + 1;
        set({ steps: newSteps, _recalcVersion: version });

        void recalculateSteps(newSteps, stepIndex, initialInput)
          .then((recalculatedSteps) => {
            if (get()._recalcVersion !== version) return;
            set({ steps: recalculatedSteps });
          })
          .catch((error: unknown) => {
            logStoreFailure({ action: 'removeStep', error });
          });
      },

      updateStepOperation: ({ stepId, operation }) => {
        const { steps, initialInput } = get();
        const stepIndex = steps.findIndex((step) => step.id === stepId);

        if (stepIndex === -1) return;

        const newSteps = [...steps];
        newSteps[stepIndex] = { ...newSteps[stepIndex], operation };
        const version = get()._recalcVersion + 1;
        set({ steps: newSteps, _recalcVersion: version });

        void recalculateSteps(newSteps, stepIndex, initialInput)
          .then((recalculatedSteps) => {
            if (get()._recalcVersion !== version) return;
            set({ steps: recalculatedSteps });
          })
          .catch((error: unknown) => {
            logStoreFailure({ action: 'updateStepOperation', error });
          });
      },

      toggleStepCollapse: (stepId) => {
        set((state) => ({
          steps: state.steps.map((step) =>
            step.id === stepId
              ? { ...step, isCollapsed: !step.isCollapsed }
              : step,
          ),
        }));
      },

      clearAllSteps: () => {
        set({
          steps: [],
          initialInput: '',
          selectedOperation: 'url-decode',
          _recalcVersion: get()._recalcVersion + 1,
        });
      },

      recalculateFromStep: (stepId) => {
        const { steps, initialInput } = get();
        const stepIndex = steps.findIndex((step) => step.id === stepId);

        if (stepIndex === -1) return;

        const version = get()._recalcVersion + 1;
        set({ _recalcVersion: version });

        void recalculateSteps(steps, stepIndex, initialInput)
          .then((recalculatedSteps) => {
            if (get()._recalcVersion !== version) return;
            set({ steps: recalculatedSteps });
          })
          .catch((error: unknown) => {
            logStoreFailure({ action: 'recalculateFromStep', error });
          });
      },
    }),
    {
      name: 'terra-decoder-store',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        initialInput: state.initialInput,
        selectedOperation: state.selectedOperation,
        inputHeight: state.inputHeight,
      }),
    },
  ),
);
async function recalculateSteps(
  steps: DecoderStep[],
  fromIndex: number,
  initialInput: string,
): Promise<DecoderStep[]> {
  const newSteps = [...steps];

  for (let i = fromIndex; i < newSteps.length; i++) {
    const step = newSteps[i];
    const input =
      i === 0 ? initialInput : newSteps[i - 1].output || newSteps[i - 1].input;

    const result = await executeOperation({ type: step.operation, input });

    newSteps[i] = {
      ...step,
      input,
      output: result.success ? result.output : input,
      error: result.error,
    };
  }

  return newSteps;
}
